package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceDraftRequest
import ai.jessie.shared.invoices.InvoiceFieldProblem
import ai.jessie.shared.invoices.InvoiceLineInput
import ai.jessie.shared.invoices.InvoiceOptionsResponse
import ai.jessie.shared.invoices.InvoicePositionOption
import ai.jessie.shared.invoices.InvoicePreviewResponse
import ai.jessie.shared.invoices.InvoiceTotalsInput
import ai.jessie.shared.invoices.InvoiceTotalsResponse
import ai.jessie.shared.invoices.InvoiceWorkInput

// Editable text as entered. The server validates, calculates and rounds; the client only checks
// what it cannot send and never derives an amount.
data class InvoiceDraft(
    val customerId: String = "",
    val currency: String = "",
    val issueDate: String = "",
    val dueDate: String = "",
    // BR-080: the proposed due date follows the invoice date until the member changes it.
    val dueDateProposed: Boolean = true,
    val lines: List<InvoiceLineDraft> = emptyList(),
)

// One proposed position (BR-073). FREE uses description, quantity, unit price and VAT;
// FIXED_PRICE and TIME_MATERIAL use project and position; TIME_MATERIAL selects eligible work
// together with the revision the member reviewed (A4).
data class InvoiceLineDraft(
    val type: String,
    val description: String = "",
    val quantity: String = "",
    val unitPrice: String = "",
    val vatRateId: String = "",
    val projectId: String = "",
    val positionId: String = "",
    val work: List<InvoiceWorkInput> = emptyList(),
)

internal const val FREE = "FREE"
internal const val FIXED_PRICE = "FIXED_PRICE"
internal const val TIME_MATERIAL = "TIME_MATERIAL"
internal val lineTypes = listOf(FREE, FIXED_PRICE, TIME_MATERIAL)

// Step 3: the organisation default currency and the proposed dates are preselected.
internal fun InvoiceOptionsResponse.newDraft() =
    InvoiceDraft(currency = defaultCurrency, issueDate = issueDate, dueDate = dueDate)

// Steps 2 and 4: a customer the server no longer offers is left empty for a new eligible choice;
// the proposed due date follows a changed invoice date unless the member set one.
internal fun InvoiceDraft.proposed(options: InvoiceOptionsResponse) =
    copy(
        customerId = customerId.takeIf { id -> options.customers.any { it.id == id } }.orEmpty(),
        dueDate = if (dueDateProposed) options.dueDate else dueDate,
    )

// Step 6: only the projects, positions and work the server offers right now stay selected.
internal fun InvoiceLineDraft.permitted(options: InvoiceOptionsResponse): InvoiceLineDraft {
    val position = options.position(projectId, positionId)
    val offered = position?.work?.associate { it.id to it.revision }.orEmpty()
    return copy(
        vatRateId = vatRateId.takeIf { id -> options.vatRates.any { it.id == id } }.orEmpty(),
        projectId = projectId.takeIf { id -> options.projects.any { it.id == id } }.orEmpty(),
        positionId = position?.id.orEmpty(),
        work =
            work.mapNotNull { entry -> offered[entry.id]?.let { InvoiceWorkInput(entry.id, it) } },
    )
}

internal fun InvoiceOptionsResponse.position(
    projectId: String,
    positionId: String,
): InvoicePositionOption? =
    projects.firstOrNull { it.id == projectId }?.positions?.firstOrNull { it.id == positionId }

// The description is sent exactly as entered; an empty one is absent so that position lines
// receive the server's default.
internal fun InvoiceDraft.request(reviewedTotals: InvoiceTotalsResponse? = null) =
    InvoiceDraftRequest(
        customerId = customerId,
        currency = currency,
        issueDate = issueDate,
        dueDate = dueDate,
        lines = lines.map { it.input() },
        reviewedTotals = reviewedTotals?.let { InvoiceTotalsInput(it.net, it.vat, it.gross) },
    )

private fun InvoiceLineDraft.input() =
    if (type == FREE)
        InvoiceLineInput(
            type = type,
            description = description,
            quantity = quantity.trim(),
            unitPrice = unitPrice.trim(),
            vatRateId = vatRateId,
        )
    else
        InvoiceLineInput(
            type = type,
            description = description.ifBlank { null },
            projectId = projectId,
            positionId = positionId,
            work = if (type == TIME_MATERIAL) work else emptyList(),
        )

// A5: only what cannot be sent is checked here; eligibility, dates and currency are the
// server's decision.
internal fun InvoiceDraft.unsendableFields(): Map<String, String> = buildMap {
    if (customerId.isBlank()) put("customerId", "REQUIRED")
    if (currency.isBlank()) put("currency", "REQUIRED")
    if (issueDate.isBlank()) put("issueDate", "INVALID")
    if (dueDate.isBlank()) put("dueDate", "INVALID")
    if (lines.isEmpty()) put("lines", "REQUIRED")
}

// A6: the position editor's own blanks; amounts, VAT and work eligibility stay server-side.
internal fun InvoiceLineDraft.unsendableFields(): Map<String, String> = buildMap {
    if (type !in lineTypes) put("line.type", "INVALID")
    if (type == FREE) {
        if (description.isBlank()) put("line.description", "REQUIRED")
        if (quantity.isBlank()) put("line.quantity", "REQUIRED")
        if (unitPrice.isBlank()) put("line.unitPrice", "REQUIRED")
        if (vatRateId.isBlank()) put("line.vatRateId", "REQUIRED")
    } else {
        if (projectId.isBlank()) put("line.projectId", "REQUIRED")
        if (positionId.isBlank()) put("line.positionId", "REQUIRED")
        if (type == TIME_MATERIAL && work.isEmpty()) put("line.work", "REQUIRED")
    }
}

// A3, A4: the server's refreshed preview drops positions without eligible work and recalculates
// changed work; the draft follows it so that the next confirmation reviews exactly those values.
internal fun InvoiceDraft.refreshed(
    preview: InvoicePreviewResponse,
    problems: List<InvoiceFieldProblem>,
): InvoiceDraft {
    val dropped =
        problems
            .filter { it.reason == "NO_ELIGIBLE_WORK" }
            .mapNotNull { LINE_FIELD.matchEntire(it.field)?.groupValues?.get(1)?.toIntOrNull() }
            .toSet()
    val kept = lines.filterIndexed { index, _ -> index !in dropped }
    val refreshed =
        kept.zip(preview.lines) { line, calculated ->
            if (line.type != TIME_MATERIAL) line
            else line.copy(work = calculated.work.map { InvoiceWorkInput(it.id, it.revision) })
        }
    return copy(lines = if (refreshed.size == kept.size) refreshed else kept)
}

internal val LINE_FIELD = Regex("lines\\[([0-9]+)\\](?:\\.(.+))?")
